import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client'
import type { TypePolicies } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import type { ErrorLink } from '@apollo/client/link/error'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { LocalState } from '@apollo/client/local-state'
import { RetryLink } from '@apollo/client/link/retry'
import { relayStylePagination } from '@apollo/client/utilities'
import { EMPTY, from, mergeMap, of, throwError } from 'rxjs'
import type { Observable } from 'rxjs'
import * as Sentry from '@sentry/react'

import { env } from '~publish/env'
import { possibleTypes } from '~publish/graphql-possible-types'
import { navigateTo } from '~publish/helpers/navigation'
import { PublicSessionLink } from '~publish/helpers/publicSessionLink'
import {
  shouldRetryRequest,
  recordFailure,
  SuccessTrackingLink,
} from '~publish/helpers/apolloRetryControl'
import {
  AbortGuardLink,
  isRequestCancelled,
  RequestCancellationLink,
} from '~publish/helpers/apolloRequestCancellation'
import {
  ALLOW_LOGGED_OUT_AUTH_REJECTION_CONTEXT_KEY,
  hasUnauthenticatedGraphQLError,
  isOnPublicPublishPage,
  isUnauthorizedNetworkError,
} from '~publish/helpers/authRejection'
import {
  redirectToAuthorize,
  redirectToImpersonateStop,
  redirectToOidcLogout,
  refreshAccessToken,
} from '~publish/helpers/oidcAuth'
import {
  isSubscriptionOperation,
  SSELink,
} from '~publish/services/realtime/sseLink'

import { OMIT_VARIABLES_FROM_SENTRY_CONTEXT_KEY } from './sentryContext'

const clientHeaders: Record<string, string> = {
  'x-buffer-client-id': 'webapp-publishing',
  'x-buffer-client-name': 'webapp-publishing',
  'x-buffer-client-version':
    env.VITE_COMMIT_HASH && env.VITE_COMMIT_HASH !== 'undefined'
      ? env.VITE_COMMIT_HASH
      : 'unknown',
}

export const baseConfig: HttpLink.Options = {
  uri: env.VITE_GRAPHQL_API,
  credentials: 'include',
  headers: clientHeaders,
}

const redirectToOrg2FASetup = (): void => {
  if (window.location.pathname.startsWith('/setup-2fa-required')) return
  navigateTo('https://publish.buffer.com/setup-2fa-required')
}

// Operation-context flag set after a single refresh+retry so a still-failing
// auth rejection on the retried request logs out instead of looping forever.
const AUTH_RETRIED_CONTEXT_KEY = '_authRetried'

/**
 * Retry predicate for {@link RetryRequestLink}. Retries transient network
 * errors when the adaptive backoff system allows it, but never retries auth
 * rejections (those flow through the error link's refresh-and-retry instead).
 */
export const shouldRetryNetworkRequest = (
  error: unknown,
  operation?: ApolloLink.Operation,
): boolean => {
  if (isRequestCancelled(error, operation)) {
    return false
  }

  if (isUnauthorizedNetworkError(error)) {
    return false
  }

  if (!shouldRetryRequest()) {
    return false
  }

  return !!error
}

/**
 * Handles an auth rejection (network 401 or GraphQL `UNAUTHENTICATED`).
 *
 * Silently refreshes the `buffer_access_token` cookie and replays the
 * operation once.
 * The public bootstrap GetPublicSession operation opts out of redirects so
 * accountless visitors can render the public shell instead. The refresh itself
 * is single-flight in {@link refreshAccessToken}, so a burst of rejected
 * operations triggers one round-trip.
 *
 * A refresh that could not be carried out — the browser is offline, or the
 * refresh endpoint could not reach the authorization server — is not evidence
 * that the session is over, so the operation fails and nobody is logged out.
 */
const handleAuthRejection = (
  operation: ApolloLink.Operation,
  forward: ApolloLink.ForwardFunction,
  error: unknown,
): Observable<ApolloLink.Result> | void => {
  const operationContext =
    typeof operation.getContext === 'function' ? operation.getContext() : {}
  const allowLoggedOut =
    isOnPublicPublishPage() &&
    operationContext[ALLOW_LOGGED_OUT_AUTH_REJECTION_CONTEXT_KEY] === true

  if (operationContext[AUTH_RETRIED_CONTEXT_KEY]) {
    if (!allowLoggedOut) {
      redirectToOidcLogout()
    }
    return
  }

  return from(refreshAccessToken()).pipe(
    mergeMap((result) => {
      if (result === 'refreshed') {
        operation.setContext({ [AUTH_RETRIED_CONTEXT_KEY]: true })
        return forward(operation)
      }

      if (result === 'unavailable') {
        return throwError(() =>
          allowLoggedOut
            ? new Error('Unable to verify your session. Please try again.', {
                cause: error,
              })
            : error,
        )
      }

      if (result === 'impersonation') {
        redirectToImpersonateStop()
        return EMPTY
      }

      if (allowLoggedOut) {
        return of({ data: { account: null } })
      }

      redirectToAuthorize()
      return EMPTY
    }),
  )
}

export const handleApolloError = ({
  error,
  operation,
  forward,
}: ErrorLink.ErrorHandlerOptions): Observable<ApolloLink.Result> | void => {
  // GraphQL errors arrive wrapped in CombinedGraphQLErrors; every other failure
  // (transport / HTTP / parse) arrives as the error itself.
  const graphQLErrors = CombinedGraphQLErrors.is(error)
    ? error.errors
    : undefined
  const networkError = CombinedGraphQLErrors.is(error) ? undefined : error

  // Track network failures for retry backoff system
  // recordFailure handles filtering to only track server errors and true network failures
  if (networkError) {
    recordFailure(networkError)
  }

  // Check for 401/Unauthorized responses
  if (isUnauthorizedNetworkError(networkError)) {
    return handleAuthRejection(operation, forward, error)
  }

  // Check for UNAUTHENTICATED errors in GraphQL errors
  if (graphQLErrors) {
    if (hasUnauthenticatedGraphQLError(graphQLErrors)) {
      return handleAuthRejection(operation, forward, error)
    }

    // OrgRequires2FASetupError → bounce the actor to the forced 2FA
    // setup page. Returning here skips Sentry capture: the error is a
    // SilentBufferError on the server and an expected control signal here.
    const hasOrg2FASetupRequired = graphQLErrors.some(
      (graphQLError) =>
        graphQLError.extensions?.code === 'ORG_REQUIRES_2FA_SETUP',
    )

    if (hasOrg2FASetupRequired) {
      redirectToOrg2FASetup()
      return
    }

    /*
     * We want to ignore certain errors that are not critical and don't need to be reported
     * For example, if the organization is not found, we don't want to report that as an error
     * This is a workaround until we have a better way to handle these errors
     */
    // Filter out specific errors
    const filteredGraphQLErrors = graphQLErrors.filter(
      (graphQLError) =>
        graphQLError.extensions?.name !== 'EngageOrganizationNotFoundError',
    )

    // If no remaining errors, suppress further error handling
    if (filteredGraphQLErrors.length === 0 && !networkError) {
      return // Prevent error propagation
    }
  }

  const operationContext =
    typeof operation?.getContext === 'function' ? operation.getContext() : {}
  const omitVariablesFromSentry =
    operationContext[OMIT_VARIABLES_FROM_SENTRY_CONTEXT_KEY] === true
  const metaData = {
    operationName: operation?.operationName ?? 'undefined',
    ...(omitVariablesFromSentry ? {} : { variables: operation?.variables }),
    graphQLErrors: graphQLErrors?.map((e) => e.message),
  }

  const isPaginationLimit =
    !networkError &&
    graphQLErrors?.some(
      (e) => e.extensions?.code === 'PAGINATION_LIMIT_EXCEEDED',
    )

  // Capture the real first GraphQL error message so Sentry issues are
  // distinguishable without digging into extras. networkError keeps priority
  // (the both-present case is unchanged).
  const reportedError =
    networkError ??
    new Error(graphQLErrors?.[0]?.message ?? 'Unknown GraphQL error')

  Sentry.withScope((scope) => {
    scope.setTag('source', 'apollo')
    scope.setTag('type', 'graphql')
    scope.setTag('operation', operation?.operationName ?? 'unknown')
    scope.setTag(
      'graphql_error_category',
      isPaginationLimit ? 'pagination_limit' : 'unknown',
    )
    scope.setExtras(metaData)
    Sentry.captureException(reportedError)
  })
}

const errorLink = onError(handleApolloError)

const AddOperationNameLink = new ApolloLink((operation, forward) => {
  const operationName = operation.operationName
  if (operationName) {
    try {
      const urlObj = new URL(
        operation.getContext().uri || baseConfig.uri,
        window.location.origin,
      )
      urlObj.searchParams.set('_o', operationName)
      operation.setContext({ uri: urlObj.href })
    } catch {
      // Something failed, but we don't want to break as this is only for debugging purposes
    }
  }
  return forward(operation)
})

/**
 * Retry Link with adaptive retry control
 *
 * Handles retries for network errors only (not GraphQL errors).
 * Retry behavior can be disabled via:
 * 1. Feature flag: `apollo-retry-disabled` (remote control during incidents)
 * 2. localStorage backoff: After 10 consecutive failures, disables for 10 minutes
 *
 * Related: CORE-3118
 */
const RetryRequestLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity, // The maximum number of milliseconds that the link should wait for any retry.
    jitter: true,
  },
  attempts: {
    max: 3, // The max number of times to try a single operation before giving up.
    retryIf: (error, operation): boolean =>
      shouldRetryNetworkRequest(error, operation),
  },
})

const NormalLink = new HttpLink(baseConfig)
const MockLink = new HttpLink({
  ...baseConfig,
  uri: env.VITE_GRAPHQL_MOCK_API,
})

/**
 * Look at the query to determine whether to send to the mock or production API.
 * https://www.apollographql.com/docs/react/api/link/introduction/#directional-composition
 */
const MockOrNormalLink = ApolloLink.split(
  (operation) => operation.getContext().mock === true,
  AbortGuardLink.concat(MockLink),
  AbortGuardLink.concat(NormalLink),
)

/**
 * Subscriptions run over Server-Sent Events against the same api-gateway URL
 * as queries and mutations, so there is no second endpoint to configure.
 */
const SubscriptionLink = new SSELink({
  url: env.VITE_GRAPHQL_API,
  credentials: 'include',
  headers: clientHeaders,
})

/**
 * Custom type policies for Apollo Client
 */
export const typePolicies: TypePolicies = {
  Idea: {
    keyFields: ['id'],
  },
  Query: {
    fields: {
      allChannelsPostInsights: {
        merge: true,
      },
      channelBenchmarks: {
        keyArgs: ['input'],
      },
      topPosts: {
        keyArgs: ['input', 'after', 'first'],
      },
      collaborationNotes: relayStylePagination(['input']),
      postsLegacy: relayStylePagination(),
      posts: relayStylePagination(['input']),
      ideas: relayStylePagination(['input']),
      post: {
        read(_, { args, toReference }) {
          return toReference({ __typename: 'Post', id: args?.input?.id })
        },
      },
      feedItems: relayStylePagination(['input']),
      teamMembers: relayStylePagination(['input']),
      // The DM inbox and a conversation's thread. Keyed on `input` so each
      // channel set, and each conversation, pages independently.
      conversations: relayStylePagination(['input']),
      conversationMessages: relayStylePagination(['input']),
      postTemplates: relayStylePagination(['input']),
      postTemplateCampaigns: relayStylePagination(['input']),
    },
  },
  Subscription: {
    fields: {
      // Each event is a new union member with no id of its own, and it
      // replaces the last one under `ROOT_SUBSCRIPTION`. `merge: false` says
      // so, and stops Apollo warning that the last event's fields are lost.
      // Entities nested in the event still normalize and merge.
      collaborationNotesChanges: {
        merge: false,
      },
      collaborationNoteSubjectChanges: {
        merge: false,
      },
    },
  },
  Post: {
    fields: {
      // A Post write that selects only part of the summary keeps the rest
      // of the cached viewer counts.
      collaborationNotesSummary: {
        merge: true,
      },
      isProcessing: {
        read(isProcessing) {
          return isProcessing ?? false
        },
      },
      // `metadata` is an unkeyed union object (one shape per network), so by
      // default each query's write REPLACES it. Post is read through many
      // narrower and wider fragments (calendar, post cards, drafts) that
      // select different metadata sibling fields for the same post — without
      // merge: true those writes would clobber each other, as happened with
      // Channel.metadata in INC-431.
      metadata: {
        merge: true,
      },
    },
  },
  ContentItem: {
    fields: {
      // `body` is an unkeyed union (`PostContent` has no id of its own). It
      // transitions from `DraftContent` to `PostContent` when a draft is
      // promoted, so a plain replace-on-typename-change write is correct —
      // merge: true gives us that for free and also covers the day a second
      // `PostContent` reader selects a different `posts` sub-set than
      // CreateContentItem/PromoteContentItemDraftToPosts do today.
      body: {
        merge: true,
      },
      // Unkeyed, and refetched on its own by the Notes host, so a refetch
      // merges into what the ContentItem already holds.
      notesSummary: {
        merge: true,
      },
    },
  },
  // `CommentFromBuffer`/`CommentFromThirdParty` (the `BaseComment` interface)
  // and the older `Comment`/`LastReply` types all carry the same
  // `metadata: CommentMetadata` shape — an unkeyed, network-specific union
  // read through several different comment fragments.
  CommentFromBuffer: {
    fields: {
      metadata: {
        merge: true,
      },
    },
  },
  CommentFromThirdParty: {
    fields: {
      metadata: {
        merge: true,
      },
    },
  },
  Comment: {
    fields: {
      metadata: {
        merge: true,
      },
    },
  },
  LastReply: {
    fields: {
      metadata: {
        merge: true,
      },
    },
  },
  // `connectionMetadata` is an unkeyed, connection-type-specific union read
  // across the feed sidebar, article list, and suggestion hooks.
  // `connectionMetadata` is an unkeyed, connection-type-specific union read
  // across the feed sidebar, article list, and suggestion hooks.
  Feed: {
    fields: {
      connectionMetadata: {
        merge: true,
      },
    },
  },
  FeedConnectionSuggestion: {
    fields: {
      connectionMetadata: {
        merge: true,
      },
    },
  },
  WebBillingDetails: {
    fields: {
      // `paymentMethodDetails` is an unkeyed union; PlanCardsVertical selects
      // only `__typename` while PaymentDetails selects the full card/bank/
      // link fields for the same billing record — without merge: true the
      // narrower write wipes PaymentDetails' cached fields.
      paymentMethodDetails: {
        merge: true,
      },
    },
  },
  Account: {
    fields: {
      preferences: {
        merge: true,
      },
    },
  },
  Channel: {
    fields: {
      // `metadata` is an unkeyed union object, so by default each query's
      // write REPLACES it. Queries selecting different union members (e.g.
      // GetAccount's MastodonMetadata vs the sidebar's BlueskyMetadata)
      // would wipe each other's fields, turning sibling cached reads
      // incomplete — the sidebar then renders an empty channel list.
      metadata: {
        merge: true,
      },
    },
  },
}

const HttpChainLink = ApolloLink.from([
  RequestCancellationLink,
  PublicSessionLink,
  errorLink,
  SuccessTrackingLink,
  AddOperationNameLink,
  RetryRequestLink,
  MockOrNormalLink,
])

const client = new ApolloClient({
  link: ApolloLink.split(
    isSubscriptionOperation,
    SubscriptionLink,
    HttpChainLink,
  ),
  cache: new InMemoryCache({
    typePolicies,
    possibleTypes,
  }),
  // `@client` fields (e.g. `Post.isProcessing`, resolved by the `typePolicies`
  // read function above) require an explicit LocalState instance.
  localState: new LocalState(),
})

export { client }
